/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useCallback, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Onboarding } from "@swan-admin/swan-web-component";
import posthog from "posthog-js";
import { UserDetailsContext } from "../../context/userDetails";
import { API_ENDPOINTS, IdntfyStore, SESSION_STORAGE_KEY, SETUP_FLOW, STEPS } from "../../vars";
import {
	convertToCentimeters,
	decodeStoredToken,
	getSessionStorage,
	handleErrorMessage,
	handleScanTimeCapture,
	handleUserExistance,
	setLocalStorageData,
	setSessionStorage,
	sleep,
} from "../../utils/util";
import EmailCompo from "../../components/emailCompo/EmailCompo";
import useShopType from "../../customHook/useShopType";
import Header from "../../components/header/Header";
import Privacy from "../../components/Privacy";
import useOtp from "../../customHook/useOtp";
import apiClient from "../../service/apiService";

function GettingStarted() {
	const { steps, shopDomain, email, setSteps, userInfo, name, gender, setUserInfo, setName, setGender, setEmail } = useContext(UserDetailsContext);
	const [isVerified] = useState(getSessionStorage(SESSION_STORAGE_KEY.IS_VERIFIED));
	const { sendOtp } = useOtp();
	const navigate = useNavigate();
	const { isIdntfy, isConsumerTryon, isMyknitever, isAdidasDemoStore } = useShopType();
	const getNextRequiredStep = useCallback(() => {
		const stepSequence = [
			{ step: STEPS.EMAIL, check: () => !decodeStoredToken() },
			{ step: STEPS.NAME, check: () => !name },
			{ step: STEPS.GENDER_TYPE, check: () => !gender },
			{ step: STEPS.PHYSICAL_ATTRIBUTES, check: () => !(userInfo?.height?.cm || convertToCentimeters(userInfo?.height?.ft, userInfo?.height?.inch)) },
		];
		const nextStep = stepSequence.find(({ check }) => check());
		return nextStep ? nextStep.step : STEPS.PRIVACY;
	}, [userInfo, name, email, gender, isVerified, isIdntfy, isMyknitever, isConsumerTryon]);

	const componentKey = useMemo(() => JSON.stringify(getNextRequiredStep()), [steps]);
	useEffect(() => {
		const requiredStep = getNextRequiredStep();
		if (steps !== requiredStep) {
			setSteps(requiredStep);
		}
	}, [getNextRequiredStep, setSteps, isVerified, isIdntfy, userInfo]);

	const handleSilentAuth = async (whatEmail) => {
		try {
			const body = {
				email: whatEmail,
				context: {
					shop_domain: shopDomain,
				},
			};
			const res = await apiClient.post(API_ENDPOINTS.auth.authSilent, body);
			setLocalStorageData(SESSION_STORAGE_KEY.JWTToken, res?.data?.magicLinkToken);
			setSessionStorage(SESSION_STORAGE_KEY.EMAIL, email|| whatEmail);
		} catch (error) {
			console.log(error);
			throw new Error(handleErrorMessage(error));
		}
	};
	const handleUserExists = async (whatEmail) => {
		try {
			if (isAdidasDemoStore || isMyknitever) {
				await handleSilentAuth(whatEmail);
				setEmail(whatEmail);
			} else {
				const res = await handleUserExistance(whatEmail);
				if (res?.data?.exists) {
					throw new Error(`Seems ${whatEmail} is already a part of SWAN.`);
				} else {
					await sendOtp({ userEmail: whatEmail });
					await sleep(5000);
				}
			}
		} catch (error) {
			throw new Error(`Seems ${whatEmail} is already a part of SWAN.`);
		}
	};

	useEffect(() => {
		if (shopDomain) {
			const  details = decodeStoredToken()
			console.log(details);
			
			if (details && details.email) {
				setEmail(details?.email);
				setSessionStorage(SESSION_STORAGE_KEY.EMAIL, details?.email);
				setSessionStorage(SESSION_STORAGE_KEY.NAME, details?.username);
				setName(details?.username);
			}
			handleScanTimeCapture({ event: `${shopDomain}/scan_started`, status: "success", email });
		}
	}, [shopDomain]);

	return (
		<div className="w-full h-full flex flex-col">
			<Header whatTypeHeader={isIdntfy ? IdntfyStore : null} />
			<div className="flex-1">
				{(() => {
					switch (steps) {
						case STEPS.NAME:
						case STEPS.EMAIL:
						case STEPS.GENDER_TYPE:
						case STEPS.PHYSICAL_ATTRIBUTES: {
							return (
								<>
									<Onboarding
										onDeviceDetected={(value) => {
											setUserInfo((prev) => ({ ...prev, device: value }));
											setSessionStorage(SESSION_STORAGE_KEY.FOCAL_LENGTH, value?.focalLength);
											setSessionStorage(SESSION_STORAGE_KEY.MODEL_NAME, value?.modelName);
											if (shopDomain) {
												const payload = { type: "auto", data: JSON.stringify({ ...value }) };
												if (email) {
													payload.email = email;
												}
												posthog.capture(`${shopDomain}/phone_detection`, payload);
											}
											console.log(value, "device detect");
										}}
										key={componentKey}
										steps={[
											{ type: "email", value: email, onStepComplete: (value) => handleUserExists(value), isVisible: !decodeStoredToken() },
											{
												type: "name",
												value: name,
												onStepComplete: (value) => {
													setSessionStorage(SESSION_STORAGE_KEY.NAME, value);
													setName(value);
												},
												isVisible: !name,
											},
											{
												type: "gender",
												value: gender,
												onStepComplete: (value) => {
													setSessionStorage(SESSION_STORAGE_KEY.GENDER, value);
													setGender(value);
												},
												isVisible: !gender,
											},
											{
												type: "height",
												value: userInfo?.height?.cm,
												onStepComplete: (value) => {
													const cm = value?.cm || convertToCentimeters(value?.ft, value?.inch);
													setSessionStorage(SESSION_STORAGE_KEY.HEIGHT, cm);
													setUserInfo((prev) => ({
														...prev,
														height: {
															cm,
														},
													}));
												},
												isVisible: !(userInfo?.height?.cm || convertToCentimeters(userInfo?.height?.ft, userInfo?.height?.inch)),
											},
										]}
										config={{
											logo: "https://scan.syze.ai/publicAssets/new-voices-over/swan_dark_logo.png",
										}}
									/>
									{STEPS.EMAIL === steps && !isAdidasDemoStore && !isMyknitever && <EmailCompo />}
								</>
							);
						}
						default: {
							return <Privacy onNext={() => navigate(SETUP_FLOW)} />;
						}
					}
				})()}
			</div>
		</div>
	);
}

export default GettingStarted;
